/**
 * Timezone-aware scheduling for recurring play event series (weekly weekday + local time).
 */

import { addMonths } from 'date-fns';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import type { PlayEventStatus, PlaySeriesSchedulePeriodMonths } from '../types/playEvent';
import { PLAY_SERIES_SCHEDULE_PERIOD_OPTIONS } from '../types/playEvent';
import { isPublicJoinablePlayEventStatus } from './playEventVisibility';

export { PLAY_SERIES_SCHEDULE_PERIOD_OPTIONS, type PlaySeriesSchedulePeriodMonths } from '../types/playEvent';

export interface PlaySeriesOccurrence {
  startsAt: Date;
  endsAt: Date;
}

export interface PlaySeriesScheduleInput {
  weekday: number;
  defaultStartTime: string;
  defaultDurationMinutes: number;
  timezone: string;
}

export interface PlaySeriesOccurrenceWindowOptions {
  /** Last calendar day (in series timezone) to scan for occurrences. */
  untilDate?: Date;
  /** Legacy day-count window when untilDate is omitted. */
  horizonDays?: number;
}

export const PLAY_SERIES_WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** Default schedule period when none is chosen (months). */
export const PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS = 12;

/** @deprecated Use PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS or series.schedulePeriodMonths */
export const PLAY_SERIES_SCHEDULE_PERIOD_MONTHS = PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS;

/** Warn operators when schedule end is within this many days. */
export const PLAY_SERIES_END_WARNING_DAYS = 30;

/** @deprecated Use PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS — kept for tests migrating from day-based horizon. */
export const PLAY_SERIES_GENERATION_HORIZON_DAYS = 365;

export function isPlaySeriesSchedulePeriodMonths(
  value: number
): value is PlaySeriesSchedulePeriodMonths {
  return (PLAY_SERIES_SCHEDULE_PERIOD_OPTIONS as readonly number[]).includes(value);
}

export function computePlaySeriesScheduleEndsAt(
  fromDate: Date = new Date(),
  months: number = PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS
): Date {
  return addMonths(fromDate, months);
}

export function extendPlaySeriesScheduleEndsAt(
  currentEndsAt: Date,
  months: number = PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS
): Date {
  return addMonths(currentEndsAt, months);
}

export function formatPlaySeriesSchedulePeriod(
  months: number = PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS
): string {
  return months === 1 ? '1 month' : `${months} months`;
}

export function formatPlaySeriesGenerationHorizon(
  days = PLAY_SERIES_GENERATION_HORIZON_DAYS
): string {
  if (days >= 365 && days % 365 === 0) {
    const years = days / 365;
    return years === 1 ? '12 months' : `${years * 12} months`;
  }
  if (days % 7 === 0 && days >= 7) {
    const weeks = days / 7;
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  }
  return `${days} days`;
}

function coerceScheduleEndsAt(scheduleEndsAt: Date | string): Date {
  const date =
    scheduleEndsAt instanceof Date ? scheduleEndsAt : new Date(scheduleEndsAt);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError('scheduleEndsAt must be a valid Date or ISO date string');
  }
  return date;
}

/** Normalize API/JSON schedule end values for comparisons and formatting. */
export function parsePlaySeriesScheduleEndsAt(scheduleEndsAt: Date | string): Date {
  return coerceScheduleEndsAt(scheduleEndsAt);
}

export function isPlaySeriesScheduleEnded(
  scheduleEndsAt: Date | string,
  now = new Date()
): boolean {
  return coerceScheduleEndsAt(scheduleEndsAt).getTime() <= now.getTime();
}

export function isPlaySeriesEndApproaching(
  scheduleEndsAt: Date | string,
  now = new Date(),
  warningDays = PLAY_SERIES_END_WARNING_DAYS
): boolean {
  const endsAt = coerceScheduleEndsAt(scheduleEndsAt);
  if (isPlaySeriesScheduleEnded(endsAt, now)) return false;
  const warningThreshold = new Date(now);
  warningThreshold.setDate(warningThreshold.getDate() + warningDays);
  return endsAt.getTime() <= warningThreshold.getTime();
}

/** Active series within the end warning window (or past end) may extend the schedule. */
export function canExtendPlaySeriesSchedule(
  series: { isActive: boolean; scheduleEndsAt?: Date | string | null },
  now = new Date()
): boolean {
  if (!series.isActive || series.scheduleEndsAt == null) return false;
  return (
    isPlaySeriesEndApproaching(series.scheduleEndsAt, now) ||
    isPlaySeriesScheduleEnded(series.scheduleEndsAt, now)
  );
}

export function describePlaySeriesEndWarning(
  scheduleEndsAt: Date | string,
  timezone?: string,
  months: number = PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS
): string {
  const endsAt = coerceScheduleEndsAt(scheduleEndsAt);
  const dateLabel = timezone
    ? formatInTimeZone(endsAt, timezone, 'd MMMM yyyy')
    : endsAt.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
  const period = formatPlaySeriesSchedulePeriod(months);
  return `This series stops generating sessions on ${dateLabel}. Extend the schedule to create sessions for another ${period}, or deactivate the series to let it end.`;
}

/** Operator-facing explanation of automatic series event generation. */
export function describePlaySeriesAutoGeneration(
  months: number = PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS
): string {
  const period = formatPlaySeriesSchedulePeriod(months);
  return `While a series is active, published play events are created automatically for the next ${period} on your chosen weekday and time. Choose 3, 6, or 12 months when creating the series. About one month before that period ends, admins receive an email and in-app reminder. You can then extend for another ${period} or deactivate the series to let it finish.`;
}

export function isPlaySeriesScheduleComplete(
  series: {
    weekday?: number | null;
    defaultStartTime?: string | null;
    defaultDurationMinutes?: number | null;
    timezone?: string | null;
  }
): boolean {
  return (
    series.weekday != null &&
    series.defaultStartTime != null &&
    series.defaultDurationMinutes != null &&
    Boolean(series.timezone?.trim())
  );
}

function normalizeHm(time: string): string {
  const trimmed = time.trim();
  if (trimmed.length >= 5) return trimmed.slice(0, 5);
  return trimmed;
}

function ymdInTimeZone(date: Date, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'yyyy-MM-dd');
}

/** Calendar weekday (0=Sun … 6=Sat) for a local YYYY-MM-DD. */
function calendarDayOfWeekFromYmd(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

function addDaysYmd(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + deltaDays);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * Build upcoming occurrence instants from fromDate through untilDate (or horizon) in series timezone.
 * Skips occurrences at or before fromDate (strictly future).
 */
export function buildPlaySeriesOccurrenceWindow(
  schedule: PlaySeriesScheduleInput,
  fromDate: Date = new Date(),
  options: PlaySeriesOccurrenceWindowOptions | number = {}
): PlaySeriesOccurrence[] {
  const resolvedOptions: PlaySeriesOccurrenceWindowOptions =
    typeof options === 'number' ? { horizonDays: options } : options;

  const timeZone = schedule.timezone;
  const weekday = schedule.weekday;
  const hm = normalizeHm(schedule.defaultStartTime);
  const durationMs = schedule.defaultDurationMinutes * 60 * 1000;
  const startYmd = ymdInTimeZone(fromDate, timeZone);
  const endYmd = resolvedOptions.untilDate
    ? ymdInTimeZone(resolvedOptions.untilDate, timeZone)
    : addDaysYmd(
        startYmd,
        resolvedOptions.horizonDays ?? PLAY_SERIES_GENERATION_HORIZON_DAYS
      );
  const occurrences: PlaySeriesOccurrence[] = [];

  for (let ymd = startYmd; ymd <= endYmd; ymd = addDaysYmd(ymd, 1)) {
    if (calendarDayOfWeekFromYmd(ymd) !== weekday) continue;

    let startsAt: Date;
    try {
      startsAt = toDate(`${ymd}T${hm}:00`, { timeZone });
    } catch {
      continue;
    }
    if (Number.isNaN(startsAt.getTime())) continue;
    if (startsAt <= fromDate) continue;
    if (resolvedOptions.untilDate && startsAt > resolvedOptions.untilDate) continue;

    occurrences.push({
      startsAt,
      endsAt: new Date(startsAt.getTime() + durationMs),
    });
  }

  return occurrences;
}

export function formatPlaySeriesRecurrenceLabel(
  weekday: number,
  defaultStartTime: string
): string {
  const dayName = PLAY_SERIES_WEEKDAY_LABELS[weekday] ?? 'Day';
  return `Every ${dayName} · ${normalizeHm(defaultStartTime)}`;
}

/** Weekly label from a session start when no series template is linked. */
export function formatPlayEventRecurrenceLabelFromStart(
  startsAt: Date | string,
  timezone: string
): string | null {
  const date = coerceEventStartsAt(startsAt);
  if (!date || !timezone.trim()) return null;
  try {
    const dayName = formatInTimeZone(date, timezone, 'EEEE');
    const hm = formatInTimeZone(date, timezone, 'HH:mm');
    return `Every ${dayName} · ${hm}`;
  } catch {
    return null;
  }
}

function coerceEventStartsAt(startsAt: Date | string): Date | null {
  const date = startsAt instanceof Date ? startsAt : new Date(startsAt);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function eventStartMs(startsAt: Date | string): number {
  return coerceEventStartsAt(startsAt)?.getTime() ?? Number.POSITIVE_INFINITY;
}

export function publicPlayEventCollapseKey(event: {
  seriesId?: string | null;
  name?: string;
  format?: string;
  side?: string;
  locationId?: string | null;
}): string {
  if (event.seriesId) return `series:${event.seriesId}`;
  return `offering:${event.name ?? ''}\0${event.format ?? ''}\0${event.side ?? ''}\0${event.locationId ?? ''}`;
}

/**
 * Public listings: one card per series, or per unlinked offering (same name/format/side/location).
 * Keep the earliest upcoming joinable occurrence in each group.
 * Unlinked copies are not a managed series: a cancelled occurrence hides later siblings.
 */
export function collapsePlayEventsToNextSeriesOccurrence<
  T extends {
    seriesId?: string | null;
    startsAt: Date | string;
    name?: string;
    format?: string;
    side?: string;
    locationId?: string | null;
    status?: PlayEventStatus;
  },
>(events: T[]): T[] {
  const sorted = [...events].sort((a, b) => eventStartMs(a.startsAt) - eventStartMs(b.startsAt));
  const byKey = new Map<string, T>();
  const cancelledUnlinkedKeys = new Set<string>();

  for (const event of sorted) {
    const key = publicPlayEventCollapseKey(event);
    if (!event.seriesId && event.status === 'cancelled') {
      cancelledUnlinkedKeys.add(key);
      continue;
    }
    if (event.status != null && !isPublicJoinablePlayEventStatus(event.status)) {
      continue;
    }
    if (cancelledUnlinkedKeys.has(key)) {
      continue;
    }
    if (!byKey.has(key)) {
      byKey.set(key, event);
    }
  }

  return [...byKey.values()].sort((a, b) => eventStartMs(a.startsAt) - eventStartMs(b.startsAt));
}
